import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface ConsentChoices {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  onConsentUpdate?: (choices: ConsentChoices) => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onConsentUpdate }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>({
    necessary: true, // Always true, cannot be disabled
    analytics: true,  // Default to true for better measurement
    marketing: false,
  });

  const applyConsent = useCallback((consentChoices: ConsentChoices) => {
    // Update Google consent mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': consentChoices.marketing ? 'granted' : 'denied',
        'ad_user_data': consentChoices.marketing ? 'granted' : 'denied',
        'ad_personalization': consentChoices.marketing ? 'granted' : 'denied',
        'analytics_storage': consentChoices.analytics ? 'granted' : 'denied',
      });
    }

    // Call parent callback if provided
    if (onConsentUpdate) {
      onConsentUpdate(consentChoices);
    }
  }, [onConsentUpdate]);

  useEffect(() => {
    // Check if user has already made consent choices
    const savedConsent = localStorage.getItem('cookieConsent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      const parsedConsent = JSON.parse(savedConsent);
      setChoices(parsedConsent);
      applyConsent(parsedConsent);
    }
  }, [applyConsent]);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setChoices(allAccepted);
    saveAndApplyConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setChoices(onlyNecessary);
    saveAndApplyConsent(onlyNecessary);
  };

  const handleSaveChoices = () => {
    saveAndApplyConsent(choices);
  };

  const saveAndApplyConsent = (consentChoices: ConsentChoices) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentChoices));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent(consentChoices);
    setShowBanner(false);
    setShowDetails(false);
  };

  const toggleChoice = (category: keyof ConsentChoices) => {
    if (category === 'necessary') return; // Cannot toggle necessary cookies
    setChoices(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner - No overlay, positioned at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-2xl z-50 border-t-2 border-purple-200">
        <div className="container mx-auto px-4 py-5">
          <div className="max-w-6xl mx-auto">
            {!showDetails ? (
              // Simple View
              <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    We value your privacy
                  </h3>
                  <p className="text-gray-600 text-sm">
                    We use cookies to improve your experience and analyze site usage. 
                    <Link to="/privacy-policy" className="text-purple-600 hover:text-purple-700 hover:underline ml-1">Learn more</Link>
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-full transition-all duration-200 text-sm"
                  >
                    Customize
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-full border border-gray-300 transition-all duration-200 text-sm"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-medium rounded-full transition-all duration-200 shadow-sm hover:shadow-md text-sm"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Detailed View
              <div>
                <div className="mb-5">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Cookie Preferences
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Choose which cookies you want to accept. Your choice will be saved for one year.
                  </p>
                </div>

                <div className="space-y-3 mb-5">
                  {/* Necessary Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">
                          Necessary Cookies
                        </h4>
                        <p className="text-sm text-gray-600">
                          Essential for the website to function properly. Enable basic 
                          functions like page navigation and access to secure areas.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={choices.necessary}
                            disabled
                            className="sr-only"
                          />
                          <div className="w-14 h-8 bg-purple-500 rounded-full cursor-not-allowed opacity-75">
                            <div className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full transition-transform" />
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 mt-1 block">Always On</span>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">
                          Analytics Cookies
                        </h4>
                        <p className="text-sm text-gray-600">
                          Help us understand how visitors interact with our website by collecting 
                          and reporting information anonymously.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => toggleChoice('analytics')}
                          className="relative"
                        >
                          <input
                            type="checkbox"
                            checked={choices.analytics}
                            readOnly
                            className="sr-only"
                          />
                          <div className={`w-14 h-8 rounded-full transition-colors ${
                            choices.analytics ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-300'
                          }`}>
                            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform shadow-sm ${
                              choices.analytics ? 'translate-x-7' : 'translate-x-1'
                            }`} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">
                          Marketing Cookies
                        </h4>
                        <p className="text-sm text-gray-600">
                          Used to deliver ads more relevant to you and your interests. They 
                          remember that you have visited this website.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => toggleChoice('marketing')}
                          className="relative"
                        >
                          <input
                            type="checkbox"
                            checked={choices.marketing}
                            readOnly
                            className="sr-only"
                          />
                          <div className={`w-14 h-8 rounded-full transition-colors ${
                            choices.marketing ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-300'
                          }`}>
                            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform shadow-sm ${
                              choices.marketing ? 'translate-x-7' : 'translate-x-1'
                            }`} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-full transition-all duration-200 text-sm"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-full border border-gray-300 transition-all duration-200 text-sm"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleSaveChoices}
                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-medium rounded-full transition-all duration-200 shadow-sm hover:shadow-md text-sm"
                  >
                    Save My Choices
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};